import React, { useState } from 'react'
import {
  Calendar as CalendarIcon,
  User,
  Hash,
  Clock,
  FileText,
  ChevronDown,
  Send,
  X,
  LayoutDashboard,
  Timer
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { PATHS } from '../config/paths'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../ui/select";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { cn } from "../ui/utils";
import { toast } from "sonner";

interface AddLeaveProps { }

const leaveStats = [
  { label: 'Total Leave', value: 24, icon: LayoutDashboard },
  { label: 'Sick Leave', value: 8, icon: CalendarIcon },
  { label: 'Casual Leave', value: 12, icon: CalendarIcon },
  { label: 'Planned Leave', value: 2, icon: CalendarIcon },
  { label: 'Remaining', value: 2, icon: Clock },
]

const AddLeave = ({ }: AddLeaveProps) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    empId: '',
    duration: 'full',
    category: 'casual',
    startDate: '',
    endDate: '',
    reason: '',
  })

  const handleValueChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.startDate || !formData.reason) {
      toast.error("Form Incomplete", { description: "Please provide all required fields." });
      return;
    }
    toast.success("Leave application submitted");
    navigate(PATHS.LEAVE_MANAGEMENT);
  }

  return (
    <div className="p-6 bg-slate-50 dark:bg-slate-950 min-h-screen">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* --- SIMPLE HEADER --- */}
        <div className="flex items-center justify-between border-b pb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Apply for Leave</h1>
            <p className="text-sm text-slate-500 mt-1">Submit a new leave request for approval</p>
          </div>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate(PATHS.LEAVE_MANAGEMENT)}>
            <X className="h-4 w-4" />
            Cancel
          </Button>
        </div>

        {/* --- BASIC STATS GRID --- */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {leaveStats.map((item, i) => (
            <Card key={i} className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm">
              <CardContent className="p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{item.label}</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">{item.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* --- MAIN FORM --- */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-md">
          <CardHeader className="border-b bg-slate-50/50 dark:bg-slate-800/50">
            <CardTitle className="text-lg font-semibold">Leave Details</CardTitle>
            <CardDescription>Fill in the form below accurately</CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="p-6 space-y-6">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-10 border-slate-200 dark:border-slate-800"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="empId">Employee ID</Label>
                  <Input
                    id="empId"
                    placeholder="e.g. EMP001"
                    value={formData.empId}
                    onChange={(e) => setFormData({ ...formData, empId: e.target.value })}
                    className="h-10 border-slate-200 dark:border-slate-800 uppercase"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Leave Category</Label>
                  <Select value={formData.category} onValueChange={(v) => handleValueChange('category', v)}>
                    <SelectTrigger className="h-10 border-slate-200 dark:border-slate-800">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="casual">Casual Leave</SelectItem>
                      <SelectItem value="sick">Sick Leave</SelectItem>
                      <SelectItem value="earned">Earned Leave</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Duration</Label>
                  <Select value={formData.duration} onValueChange={(v) => handleValueChange('duration', v)}>
                    <SelectTrigger className="h-10 border-slate-200 dark:border-slate-800">
                      <SelectValue placeholder="Full Day" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Full Day</SelectItem>
                      <SelectItem value="half">Half Day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="h-10 border-slate-200 dark:border-slate-800"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="h-10 border-slate-200 dark:border-slate-800"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Reason for Leave</Label>
                <Textarea
                  id="reason"
                  placeholder="Provide a brief reason..."
                  rows={4}
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="border-slate-200 dark:border-slate-800 resize-none"
                />
              </div>

            </CardContent>

            <div className="p-6 border-t bg-slate-50/50 dark:bg-slate-800/50 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(PATHS.LEAVE_MANAGEMENT)}>
                Discard
              </Button>
              <Button type="submit" className="px-8">
                Submit Request
              </Button>
            </div>
          </form>
        </Card>

      </div>
    </div>
  )
}

export default AddLeave
